import { logger } from "@mgdx-libs/logger";
import { Mutex } from "async-mutex";

import {
  callGetCameraPermissionStatus,
  callGetMicrophonePermissionStatus,
  callRequestCameraPermission,
  callRequestMicrophonePermission,
} from "../../app-bridge/publisher";
import {
  type GetCameraPermissionStatusData,
  GetMicrophonePermissionStatusData,
  removeYqbSendCameraPermissionRequestResultHandler,
  removeYqbSendCameraPermissionStatusHandler,
  removeYqbSendMicrophonePermissionRequestResultHandler,
  removeYqbSendMicrophonePermissionStatusHandler,
  setYqbSendCameraPermissionRequestResultHandler,
  setYqbSendCameraPermissionStatusHandler,
  setYqbSendMicrophonePermissionRequestResultHandler,
  setYqbSendMicrophonePermissionStatusHandler,
} from "../../app-bridge/subscriber";
import { isInMccmApp } from "../../utils/isInMccmApp";

export class AppBridgePermissionManagerError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AppBridgePermissionManagerError";
  }
}

type PermissionStatus = {
  camera: GetCameraPermissionStatusData["cameraPermissionStatus"] | null;
  microphone: GetMicrophonePermissionStatusData["microphonePermissionStatus"] | null;
};

type Device = "camera" | "microphone";
type Devices = Array<Device>;

export class AppBridgePermissionManager {
  private mutex: Mutex;
  private watchMutex: Mutex;
  private timeoutMs = 60_000; // 1分
  private devices: Devices = [];
  private intervalId: number | null;
  permitting = false;

  permissionStatus: PermissionStatus = {
    camera: null,
    microphone: null,
  };

  constructor() {
    this.mutex = new Mutex();
    this.watchMutex = new Mutex();
    this.intervalId = null;
  }

  static canUseAppBridge(): boolean {
    return isInMccmApp();
  }

  async permit({ devices }: { devices: Devices }): Promise<void> {
    if (this.permitting) return;

    this.permitting = true;

    return this.mutex.runExclusive(async () => {
      logger.debug("[AppBridgePermissionManager] start permit", devices);

      this.devices = devices;
      // タイムアウト競合を明示的に組み合わせ
      return Promise.race([this.permitMain(), this.permitTimer()]).finally(() => {
        if (this.intervalId) {
          window.clearInterval(this.intervalId);
        }
        removeYqbSendCameraPermissionStatusHandler();
        removeYqbSendMicrophonePermissionStatusHandler();
        removeYqbSendCameraPermissionRequestResultHandler();
        removeYqbSendMicrophonePermissionRequestResultHandler();
        this.permitting = false;
      });
    });
  }

  private isDenied(): boolean {
    if (this.devices.includes("camera") && this.devices.includes("microphone")) {
      if (this.permissionStatus.camera && this.permissionStatus.microphone) {
        return (
          ["denied", "restricted"].includes(this.permissionStatus.camera) &&
          ["denied", "restricted"].includes(this.permissionStatus.microphone)
        );
      }
    } else if (this.devices.includes("camera")) {
      if (this.permissionStatus.camera) {
        return ["denied", "restricted"].includes(this.permissionStatus.camera);
      }
    }

    return false;
  }

  private async permitMain(): Promise<void> {
    logger.debug("[AppBridgePermissionManager] Check permission");
    await this.getPermissionStatus();

    if (this.isDenied()) {
      logger.debug("[AppBridgePermissionManager] resolved");
      return;
    }

    logger.debug("[AppBridgePermissionManager] request permission");
    await this.requestPermission();

    this.intervalId = window.setInterval(() => {
      this.watchMutex.runExclusive(async () => {
        await this.getPermissionStatus();
        if (this.isDenied()) {
          logger.debug("[AppBridgePermissionManager] resolved");
          return;
        }
      });
    }, 500);
  }

  private async getPermissionStatus() {
    // このクラスでは Promise.all は使用禁止。
    // デバイスごとの権限取得は順次 await で行う（並列実行にしないこと）。
    if (this.devices.includes("camera")) {
      await this.getCameraPermissionStatus();
    }
    if (this.devices.includes("microphone")) {
      await this.getMicrophonePermissionStatus();
    }
  }

  private async getCameraPermissionStatus() {
    return new Promise<void>((resolve, reject) => {
      setYqbSendCameraPermissionStatusHandler(
        (data) => {
          logger.debug("[AppBridgePermissionManager] yqbSendCameraPermissionStatus: %o", data);
          this.permissionStatus.camera = data.cameraPermissionStatus;
          resolve();
        },
        () => {
          reject(new AppBridgePermissionManagerError("yqbSendCameraPermissionStatus error"));
        }
      );
      callGetCameraPermissionStatus();
    });
  }

  private async getMicrophonePermissionStatus() {
    return new Promise<void>((resolve, reject) => {
      setYqbSendMicrophonePermissionStatusHandler(
        (data) => {
          logger.debug("[AppBridgePermissionManager] yqbSendMicrophonePermissionStatus: %o", data);
          this.permissionStatus.microphone = data.microphonePermissionStatus;
          resolve();
        },
        () => {
          reject(new AppBridgePermissionManagerError("yqbSendMicrophonePermissionStatus error"));
        }
      );
      callGetMicrophonePermissionStatus();
    });
  }

  private async requestPermission() {
    // このクラスでは Promise.all は使用禁止。
    // 権限リクエストも順次実行し、並列にしないこと。
    await this.requestCameraPermission();
    if (this.devices.includes("camera") && this.devices.includes("microphone")) {
      await this.requestMicrophonePermission();
    }
  }

  private async requestCameraPermission() {
    return new Promise<void>((resolve, reject) => {
      setYqbSendCameraPermissionRequestResultHandler(
        (data) => {
          logger.debug("[AppBridgePermissionManager] yqbSendCameraPermissionRequestResult: %o", data);
          resolve();
        },
        () => {
          reject(new AppBridgePermissionManagerError("yqbSendCameraPermissionRequestResult error"));
        }
      );

      callRequestCameraPermission();
    });
  }

  private async requestMicrophonePermission() {
    return new Promise<void>((resolve, reject) => {
      setYqbSendMicrophonePermissionRequestResultHandler(
        (data) => {
          logger.debug("[AppBridgePermissionManager] yqbSendMicrophonePermissionRequestResult: %o", data);
          resolve();
        },
        () => {
          reject(new AppBridgePermissionManagerError("yqbSendMicrophonePermissionRequestResult error"));
        }
      );

      callRequestMicrophonePermission();
    });
  }

  private async permitTimer(): Promise<void> {
    return new Promise<void>((_resolve, reject) =>
      setTimeout(() => reject(new AppBridgePermissionManagerError("Timeout")), this.timeoutMs)
    );
  }
}
